"use client";

import { signIn } from "next-auth/react";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useForm } from "react-hook-form";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { FiMail, FiLock, FiLogIn, FiUserPlus } from "react-icons/fi";
import { useRouter, useSearchParams } from "next/navigation";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import { LoginSchema } from "@/app/schemas";
import { login } from "@/app/actions/login";
import { ButtonS } from "@/components/ui/button";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { FormError } from "@/app/components/FormError";
import { FormSuccess } from "@/app/components/FormSucess";
import Heading from "../components/Heading";
import Container from "../components/Container";

interface LoginSelectProps {
    currentUser?: any;
}

const LoginClient: React.FC<LoginSelectProps> = () => {
    const router = useRouter();
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState<{ error?: string; success?: string }>(
        {},
    );
    const searchParams = useSearchParams();
    const urlError =
        searchParams?.get("error") === "OAuthAccountNotLinked"
            ? "Email already in use with a different provider!"
            : "";


    const form = useForm<z.infer<typeof LoginSchema>>({
        resolver: zodResolver(LoginSchema),
        defaultValues: {
            email: "",
            password: "",
        },
    });

    const onSubmit = async (values: z.infer<typeof LoginSchema>) => {
        if (isSubmitting) return;

        setIsSubmitting(true);
        setMessage({});

        try {
            const data = await login(values);

            if (data.error) {
                setMessage({ error: data.error });
                return;
            }

            if (data.success === "Confirmation email sent!") {
                form.reset();
                setMessage({ success: data.success });
                return;
            }

            const result = await signIn("credentials", {
                ...values,
                redirect: false,
            });

            if (result?.error) {
                setMessage({ error: "Invalid credentials" });
                return;
            }

            toast.success("Logged in!");
            // router.refresh();
            router.push(searchParams?.get("callbackUrl") === "/publish" ? "/publish" : "/");
        } catch (error) {
            setMessage({ error: "Something went wrong" });
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        if (urlError) {
            toast.error(urlError);
        }
    }, [urlError]);

    return (
        <div className="min-h-screen bg-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full">
                {/* Logo/Icon Section */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-cyan-500/10 rounded-2xl mb-4 border border-cyan-500/20">
                        <FiLogIn className="text-cyan-400" size={32} />
                    </div>
                    <h1 className="text-3xl font-bold text-white mb-2">
                        Welcome back
                    </h1>
                    <p className="text-gray-400 text-sm">
                        Login to your account to continue
                    </p>
                </div>

                {/* Login Card */}
                <div className="bg-gray-800 border border-gray-700 rounded-2xl shadow-2xl p-8">
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                            <div className="space-y-5">
                                {/* Email Field */}
                                <FormField
                                    control={form.control}
                                    name="email"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-gray-300 text-sm font-medium flex items-center gap-2">
                                                <FiMail size={16} className="text-cyan-400" />
                                                Email Address
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    disabled={isSubmitting}
                                                    placeholder="you@example.com"
                                                    type="email"
                                                    className="bg-gray-900 border-gray-700 text-white placeholder-gray-500 h-12 pl-4 pr-4 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                                                />
                                            </FormControl>
                                            <FormMessage className="text-red-400 text-xs mt-1" />
                                        </FormItem>
                                    )}
                                />

                                {/* Password Field */}
                                <FormField
                                    control={form.control}
                                    name="password"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-gray-300 text-sm font-medium flex items-center gap-2">
                                                <FiLock size={16} className="text-cyan-400" />
                                                Password
                                            </FormLabel>
                                            <div className="relative">
                                                <FormControl>
                                                    <Input
                                                        {...field}
                                                        disabled={isSubmitting}
                                                        placeholder="Enter your password"
                                                        type={showPassword ? "text" : "password"}
                                                        className="bg-gray-900 border-gray-700 text-white placeholder-gray-500 h-12 pl-4 pr-12 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                                                    />
                                                </FormControl>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassword((prev) => !prev)}
                                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-cyan-400 transition-colors"
                                                >
                                                    {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                                                </button>
                                            </div>
                                            <FormMessage className="text-red-400 text-xs mt-1" />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            {/* Forgot Password Link */}
                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    onClick={() => router.push("/auth/forgotpassword")}
                                    className="text-sm text-cyan-400 hover:text-cyan-300 font-medium transition-colors"
                                >
                                    Forgot password?
                                </button>
                            </div>

                            {/* Error/Success Messages */}
                            <FormError message={message.error || urlError} />
                            <FormSuccess message={message.success} />

                            {/* Submit Button */}
                            <ButtonS
                                type="submit"
                                disabled={isSubmitting}
                                className="h-12 w-full rounded-xl bg-cyan-500 text-black font-semibold text-base hover:bg-cyan-600 transition-all shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/40 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                            >
                                {isSubmitting ? (
                                    <div className="flex items-center justify-center gap-2">
                                        <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                                        Logging in...
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center gap-2">
                                        <FiLogIn size={20} />
                                        Login
                                    </div>
                                )}
                            </ButtonS>
                        </form>
                    </Form>
                </div>

                {/* Register Link */}
                <div className="mt-6 text-center">
                    <p className="text-gray-400 text-sm">
                        Don't have an account?{" "}
                        <button
                            onClick={() => router.push(searchParams?.get("callbackUrl") === "/publish" ? "/register?callbackUrl=/publish" : "/register")}
                            className="text-cyan-400 hover:text-cyan-300 font-semibold transition-colors inline-flex items-center gap-1"
                        >
                            Create account
                            <FiUserPlus size={16} />
                        </button>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default LoginClient;
